/**
 * This tab's started session, `{ userKey, sessionId }`: kept in memory for the
 * page and in sessionStorage across reloads. Policy: docs/architecture/context.md
 * "Portal session".
 */

const STORAGE_KEY = "customer-portal:session";

export interface SessionRecord {
  userKey: string;
  sessionId: string;
}

let memoryRecord: SessionRecord | null = null;

function parse(raw: string | null): SessionRecord | null {
  try {
    const value: unknown = raw === null ? null : JSON.parse(raw);
    const { userKey, sessionId } = (value ?? {}) as Partial<SessionRecord>;
    return typeof userKey === "string" && typeof sessionId === "string"
      ? { userKey, sessionId }
      : null;
  } catch {
    return null;
  }
}

// Storage calls sit inside `try`: sandboxed iframes and blocked cookies throw
// on reading `window.sessionStorage` itself.

export function readSessionRecord(): SessionRecord | null {
  if (memoryRecord) return memoryRecord;
  try {
    return parse(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function saveSessionRecord(record: SessionRecord) {
  memoryRecord = record;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Memory only: the next page load starts a session.
  }
}

export function clearSessionRecord() {
  memoryRecord = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to clear.
  }
}
